import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ReceiptText, Loader2, Printer, Download, Trash2, FileUp, Banknote, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { printInvoice, downloadInvoicePdf } from "@/lib/invoicePrint";
import { SoDocButton } from "@/components/GenerateBolDialog";

const num = (v) => (Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtShort = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const today = () => new Date().toISOString().slice(0, 10);
const addDays = (d, n) => {
  const x = new Date(`${d}T00:00:00`);
  x.setDate(x.getDate() + n);
  return x.toISOString().slice(0, 10);
};
const SQ_BTN = "flex h-7 w-7 shrink-0 items-center justify-center rounded-sm border transition-colors";

export function GenerateInvoiceDialog({ so, customers, getCompany, onOpenChange, onSaved }) {
  const [number, setNumber] = useState("");
  const [invDate, setInvDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState([]);
  const [source, setSource] = useState("order");
  const [saving, setSaving] = useState(false);

  const dos = so?._dv?.dos || [];

  const buildRows = (soObj, src) => {
    const bol = src !== "order" ? dos.find((d) => d.id === src)?.full : null;
    return (soObj.lines || []).map((l) => {
      let qty = l.cases || 0, checked = true;
      if (bol) {
        const bl = (bol.lines || []).find((x) => x.so_line_id === l.id)
          || (bol.lines || []).find((x) => !x.so_line_id && x.product_id === l.product_id && (x.design_id || "") === (l.design_id || "") && (x.config_id || "") === (l.config_id || ""));
        qty = bl ? bl.cases || 0 : 0;
        checked = qty > 0;
      }
      return {
        line: l, checked,
        qty: String(qty),
        price: l.price != null && l.price !== 0 ? String(l.price) : "",
      };
    });
  };

  useEffect(() => {
    if (!so) return;
    const n = (so.invoices || []).length;
    const base = `INV-${(so.so_number || "").replace(/^SO-/, "")}`;
    setNumber(n ? `${base}-${n + 1}` : base);
    const d = today();
    setInvDate(d);
    const netDays = Number((so.terms || "").match(/net\s*(\d+)/i)?.[1] || 0);
    setDueDate(netDays ? addDays(d, netDays) : d);
    setNotes("");
    setSource("order");
    setRows(buildRows(so, "order"));
    setSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [so]);

  if (!so) return null;

  const pickSource = (src) => {
    setSource(src);
    setRows(buildRows(so, src));
    if (src !== "order") {
      const bol = dos.find((d) => d.id === src);
      if (bol) toast.info(`Quantities pulled from ${bol.full?.bol_number || bol.do_number}`);
    }
  };

  const setRow = (i, patch) => setRows((prev) => prev.map((r, x) => (x === i ? { ...r, ...patch } : r)));
  const selected = rows.filter((r) => r.checked && Number(r.qty) > 0);
  const total = selected.reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.price) || 0), 0);
  const missingPrice = selected.some((r) => !Number(r.price));

  const generate = async () => {
    if (!number.trim()) { toast.error("Enter an invoice number"); return; }
    if (!selected.length) { toast.error("Select at least one line with a quantity"); return; }
    setSaving(true);
    try {
      const { data: inv } = await api.post(`/sales-orders/${so.id}/invoices`, {
        number: number.trim(), invoice_date: invDate, due_date: dueDate, notes,
        lines: selected.map(({ line: l, qty, price }) => ({
          description: l.product_name, design: l.design_name || "",
          pack: l.case_pack ? `${num(l.case_pack)}-Pack` : "",
          cases: Number(qty), price: Number(price) || 0,
          product_id: l.product_id || "", design_id: l.design_id || "",
        })),
      });
      toast.success(`${inv.number} created for ${so.so_number} — ${money(inv.total)}`);
      printInvoice(inv, so, await getCompany(), customers.find((c) => c.id === so.customer_id) || null);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to generate invoice");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!so} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-sm sm:max-w-2xl" data-testid="generate-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Generate Invoice — {so.so_number}</DialogTitle>
          <DialogDescription>{so.customer_name} · PO {so.customer_po || "—"}{so.terms ? ` · ${so.terms}` : ""}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Invoice #</p>
            <Input value={number} onChange={(e) => setNumber(e.target.value)} className="h-9 rounded-sm" data-testid="invoice-number-input" />
          </div>
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Invoice Date</p>
            <Input type="date" value={invDate} onChange={(e) => setInvDate(e.target.value)} className="h-9 rounded-sm" data-testid="invoice-date-input" />
          </div>
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Due Date</p>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="h-9 rounded-sm" data-testid="invoice-due-input" />
          </div>
        </div>

        {dos.length > 0 && (
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Bill quantities from</p>
            <Select value={source} onValueChange={pickSource}>
              <SelectTrigger className="h-9 rounded-sm" data-testid="invoice-source-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="order">Full order — {num(so.total_cases)} cases</SelectItem>
                {dos.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {(d.full?.bol_number || d.do_number)} — {num(d.cases)} cases{d.pickup_date ? ` — ${fmtShort(d.pickup_date)}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {source !== "order" && <p className="mt-1 text-[11px] text-slate-400">Only what shipped on this BOL is billed — lines not on it are unchecked.</p>}
          </div>
        )}

        <div className="border border-slate-200">
          <div className="flex items-center border-b border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-600">
            <span>Invoice lines</span>
            <span className="ml-auto flex gap-6"><span className="w-20 text-right">Cases</span><span className="w-24 text-right">$ / Case</span></span>
          </div>
          {rows.map((r, i) => {
            const l = r.line;
            return (
              <div key={l.id} className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 last:border-b-0" data-testid={`invoice-line-${l.id}`}>
                <Checkbox checked={r.checked} onCheckedChange={(v) => setRow(i, { checked: !!v })} data-testid={`invoice-line-check-${l.id}`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-slate-800">{l.product_name}{l.design_name ? ` | ${l.design_name}` : ""}{l.case_pack ? ` | ${num(l.case_pack)}-Pack` : ""}</p>
                  <p className="text-xs tabular-nums text-slate-400">{money((Number(r.qty) || 0) * (Number(r.price) || 0))}</p>
                </div>
                <Input type="number" min="0" value={r.qty} disabled={!r.checked}
                  onChange={(e) => setRow(i, { qty: e.target.value })}
                  className="h-8 w-20 rounded-sm text-right tabular-nums" data-testid={`invoice-line-qty-${l.id}`} />
                <Input type="number" min="0" step="0.01" value={r.price} disabled={!r.checked} placeholder="0.00"
                  onChange={(e) => setRow(i, { price: e.target.value })}
                  className="h-8 w-24 rounded-sm text-right tabular-nums" data-testid={`invoice-line-price-${l.id}`} />
              </div>
            );
          })}
        </div>

        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Invoice notes (optional)" className="h-9 rounded-sm" data-testid="invoice-notes-input" />

        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold tabular-nums text-slate-700" data-testid="invoice-total">
            Total {money(total)}
            {missingPrice && <span className="ml-2 text-xs font-medium text-orange-600">some lines have no price</span>}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="invoice-cancel">Cancel</Button>
            <Button size="sm" className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" disabled={saving || !selected.length} onClick={generate} data-testid="invoice-generate">
              {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ReceiptText className="mr-1.5 h-3.5 w-3.5" />} Generate Invoice
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SoInvoiceCell({ so, customers, getCompany, onGenerate, onChanged }) {
  const invs = so.invoices || [];
  const customer = customers.find((c) => c.id === so.customer_id) || null;
  const [payFor, setPayFor] = useState(null);
  const [payAmt, setPayAmt] = useState("");
  const [payDate, setPayDate] = useState("");
  const [payNote, setPayNote] = useState("");
  const [saving, setSaving] = useState(false);
  const act = async (fn, inv) => fn(inv, so, await getCompany(), customer);
  const paidOf = (inv) => (inv.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const remove = async (inv) => {
    try {
      await api.delete(`/sales-orders/${so.id}/invoices/${inv.id}`);
      toast.success(`${inv.number} removed`);
      onChanged?.();
    } catch { toast.error("Failed to remove invoice"); }
  };
  const openPay = (inv) => {
    setPayFor(inv.id);
    setPayAmt(String(Math.max(0, (inv.total || 0) - paidOf(inv)).toFixed(2)));
    setPayDate(today());
    setPayNote("");
  };
  const savePay = async (inv) => {
    if (!Number(payAmt)) { toast.error("Enter a payment amount"); return; }
    setSaving(true);
    try {
      await api.post(`/sales-orders/${so.id}/invoices/${inv.id}/payments`, { amount: Number(payAmt), date: payDate, note: payNote });
      toast.success(`${money(Number(payAmt))} payment recorded on ${inv.number}`);
      setPayFor(null);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to record payment");
    } finally { setSaving(false); }
  };
  const removePay = async (inv, pay) => {
    try {
      await api.delete(`/sales-orders/${so.id}/invoices/${inv.id}/payments/${pay.id}`);
      toast.success("Payment removed");
      onChanged?.();
    } catch { toast.error("Failed to remove payment"); }
  };
  const emailInvoice = async (inv) => {
    const bal = (inv.total || 0) - paidOf(inv);
    await act(downloadInvoicePdf, inv);
    const emails = (customer?.contacts || []).filter((c) => c.email && !c.suppress_email).map((c) => c.email);
    if (!emails.length) toast.warning("No customer contacts with an email — add one on the Contacts page.");
    const subject = `Invoice ${inv.number}${so.customer_po ? ` — PO ${so.customer_po}` : ""}`;
    const body = [
      "Hi,",
      "",
      `Please find attached invoice ${inv.number}${so.customer_po ? ` for PO ${so.customer_po}` : ""}.`,
      "",
      `Amount due: ${money(bal)}`,
      inv.due_date ? `Due date: ${fmtShort(inv.due_date)}` : "",
      so.terms ? `Terms: ${so.terms}` : "",
      "",
      "Wire instructions are on the invoice. Please reference the invoice number with your payment.",
      "",
      "Thank you,",
    ].filter((l) => l !== null).join("\n");
    toast.info(`${inv.number}.pdf downloaded — attach it to the email`);
    window.location.href = `mailto:${emails.join(";")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex items-center gap-1.5">
        <button title="Generate Invoice" onClick={(e) => { e.stopPropagation(); onGenerate(); }}
          className={`${SQ_BTN} border-slate-200 bg-white text-slate-500 hover:border-[#002FA7] hover:text-[#002FA7]`}
          data-testid={`so-generate-invoice-${so.id}`}>
          <ReceiptText className="h-3.5 w-3.5" />
        </button>
        {invs.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <button className="whitespace-nowrap text-xs font-semibold text-[#002FA7] hover:underline" onClick={(e) => e.stopPropagation()} data-testid={`so-invoice-count-${so.id}`}>
                {invs.length} Inv{invs.length !== 1 ? "s" : ""}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto min-w-[420px] rounded-sm p-0" onClick={(e) => e.stopPropagation()}>
              <p className="border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-600">Invoices — {so.so_number}</p>
              {invs.map((inv) => {
                const paid = paidOf(inv);
                const bal = (inv.total || 0) - paid;
                return (
                  <div key={inv.id} className="border-b border-slate-100 last:border-b-0" data-testid={`so-invoice-item-${inv.id}`}>
                    <div className="flex items-center gap-2.5 px-3 py-2 text-xs">
                      <span className="font-semibold text-slate-800">{inv.number}</span>
                      <span className="text-slate-400">{fmtShort(inv.invoice_date)}</span>
                      <span className="tabular-nums font-semibold text-emerald-700">{money(inv.total)}</span>
                      {paid > 0 && (bal <= 0.004
                        ? <span className="rounded-sm bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700" data-testid={`so-invoice-paid-${inv.id}`}>PAID</span>
                        : <span className="whitespace-nowrap text-[10px] font-bold text-amber-600" data-testid={`so-invoice-balance-${inv.id}`}>{money(bal)} due</span>)}
                      <span className="ml-auto flex gap-1">
                        <button title="Record payment" className="p-1 text-slate-500 hover:text-emerald-700" onClick={() => (payFor === inv.id ? setPayFor(null) : openPay(inv))} data-testid={`so-invoice-pay-${inv.id}`}><Banknote className="h-3.5 w-3.5" /></button>
                        <button title="View / print" className="p-1 text-slate-500 hover:text-[#002FA7]" onClick={() => act(printInvoice, inv)} data-testid={`so-invoice-print-${inv.id}`}><Printer className="h-3.5 w-3.5" /></button>
                        <button title="Download PDF" className="p-1 text-slate-500 hover:text-[#002FA7]" onClick={() => act(downloadInvoicePdf, inv)} data-testid={`so-invoice-download-${inv.id}`}><Download className="h-3.5 w-3.5" /></button>
                        <button title="Email invoice to customer (PDF downloads — attach it)" className="p-1 text-slate-500 hover:text-[#002FA7]" onClick={() => emailInvoice(inv)} data-testid={`so-invoice-email-${inv.id}`}><Mail className="h-3.5 w-3.5" /></button>
                        <button title="Delete invoice" className="p-1 text-slate-400 hover:text-red-600" onClick={() => remove(inv)} data-testid={`so-invoice-delete-${inv.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </span>
                    </div>
                    {(inv.payments || []).map((p) => (
                      <div key={p.id} className="flex items-center gap-2 px-3 pb-1.5 pl-5 text-[11px] text-slate-500" data-testid={`so-invoice-payment-${p.id}`}>
                        <Banknote className="h-3 w-3 text-emerald-600" />
                        <span className="tabular-nums font-semibold text-emerald-700">{money(p.amount)}</span>
                        <span>{fmtShort(p.date)}</span>
                        {p.note && <span className="truncate text-slate-400">{p.note}</span>}
                        <button title="Remove payment" className="ml-auto p-0.5 text-slate-300 hover:text-red-600" onClick={() => removePay(inv, p)} data-testid={`so-invoice-payment-delete-${p.id}`}><Trash2 className="h-3 w-3" /></button>
                      </div>
                    ))}
                    {payFor === inv.id && (
                      <div className="flex items-center gap-1.5 border-t border-slate-100 bg-slate-50 px-3 py-2">
                        <Input type="number" min="0" step="0.01" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} placeholder="Amount"
                          className="h-7 w-24 rounded-sm text-right text-xs tabular-nums" data-testid={`so-invoice-pay-amount-${inv.id}`} />
                        <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)}
                          className="h-7 w-32 rounded-sm text-xs" data-testid={`so-invoice-pay-date-${inv.id}`} />
                        <Input value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="Note (optional)"
                          className="h-7 flex-1 rounded-sm text-xs" data-testid={`so-invoice-pay-note-${inv.id}`} />
                        <Button size="sm" className="h-7 rounded-sm bg-emerald-700 px-2.5 text-xs text-white hover:bg-emerald-800" disabled={saving}
                          onClick={() => savePay(inv)} data-testid={`so-invoice-pay-save-${inv.id}`}>
                          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Record"}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </PopoverContent>
          </Popover>
        )}
      </div>
      <SoDocButton so={so} kind="invoice" icon={FileUp} label="Customer Invoice" onChanged={onChanged} />
    </div>
  );
}
